import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import type { LayerServiceField } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

/**
 * Pure builders behind the inspector's guided widgets: derive the current
 * state from the draft document and produce complete replacement values for
 * `applyInspectorEdit`. Everything metadata-driven stays grounded in the
 * probed service fields — the same "never invent field names" rule the AI
 * follows.
 */

export function parseDraftObject(text: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    // Half-typed JSON: widgets freeze on the last good state upstream.
  }
  return null
}

export function parseDraftArray(text: string): unknown[] | null {
  try {
    const parsed: unknown = JSON.parse(text)
    if (Array.isArray(parsed)) return parsed
  } catch {
    // See parseDraftObject.
  }
  return null
}

// --- Scale range ---

/**
 * Common Norwegian map scales, "no limit" first. Slider positions map onto
 * this list — a raw 1:x slider is unusable over six orders of magnitude.
 */
export const SCALE_STOPS = [
  0, 500, 1_000, 2_500, 5_000, 10_000, 25_000, 50_000, 100_000, 250_000,
  500_000, 1_000_000, 5_000_000,
] as const

export function formatScaleStop(scale: number) {
  if (scale === 0) return "No limit"
  return `1:${scale.toLocaleString("en-GB")}`
}

/** The stop index closest to a configured scale (for the slider position). */
export function nearestScaleStopIndex(value: unknown): number {
  const scale = typeof value === "number" ? value : 0
  let best = 0
  for (let index = 0; index < SCALE_STOPS.length; index++) {
    if (
      Math.abs(SCALE_STOPS[index] - scale) < Math.abs(SCALE_STOPS[best] - scale)
    ) {
      best = index
    }
  }
  return best
}

// --- Popup fields ---

type FieldInfoLike = { fieldName?: unknown; label?: unknown }

/** Field names currently shown in the draft's popup, in template order. */
export function getSelectedPopupFields(
  configuration: Record<string, unknown>,
): string[] {
  const template = configuration.popupTemplate as
    | Record<string, unknown>
    | undefined
  if (!Array.isArray(template?.fieldInfos)) return []
  return (template.fieldInfos as FieldInfoLike[])
    .map((info) => (typeof info.fieldName === "string" ? info.fieldName : ""))
    .filter(Boolean)
}

/**
 * The complete replacement `popupTemplate` for a new field selection.
 * Existing fieldInfos keep their entry untouched (labels, formats); newly
 * checked fields get a starter entry labeled with the service alias.
 */
export function buildPopupTemplateValue(
  configuration: Record<string, unknown>,
  serviceFields: LayerServiceField[],
  selected: string[],
  fallbackTitle: string,
): Record<string, unknown> {
  const template = (configuration.popupTemplate ?? {}) as Record<
    string,
    unknown
  >
  const existing = Array.isArray(template.fieldInfos)
    ? (template.fieldInfos as FieldInfoLike[])
    : []

  const fieldInfos = selected.map((fieldName) => {
    const kept = existing.find((info) => info.fieldName === fieldName)
    if (kept) return kept
    const serviceField = serviceFields.find((field) => field.name === fieldName)
    return {
      fieldName,
      label: serviceField?.alias || fieldName,
      visible: true,
    }
  })

  return {
    // content [{type:"fields"}] renders the fieldInfos as the popup body —
    // without it a template with only fieldInfos shows an empty popup.
    content: [{ type: "fields" }],
    ...template,
    title: typeof template.title === "string" ? template.title : fallbackTitle,
    fieldInfos,
  }
}

// --- Renderer presets ---

/** Hex "#rrggbb" to the ArcGIS JSON color array [r, g, b, alpha(0-255)]. */
function hexToArcgisColor(hex: string, alpha: number): number[] {
  const value = Number.parseInt(hex.slice(1), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, alpha]
}

type GeometryKind = "polygon" | "polyline" | "point"

export function getGeometryKind(
  geometryType: string | undefined,
): GeometryKind | null {
  if (!geometryType) return null
  if (/polygon/i.test(geometryType)) return "polygon"
  if (/(polyline|line)/i.test(geometryType)) return "polyline"
  if (/(point|multipoint)/i.test(geometryType)) return "point"
  return null
}

function buildSymbol(kind: GeometryKind, hex: string): Record<string, unknown> {
  if (kind === "polygon") {
    return {
      type: "simple-fill",
      color: hexToArcgisColor(hex, 140),
      outline: {
        color: hexToArcgisColor(DATA_VIZ.brand.greenDeep, 220),
        width: 0.6,
      },
    }
  }
  if (kind === "polyline") {
    return {
      type: "simple-line",
      color: hexToArcgisColor(hex, 220),
      width: 1.6,
    }
  }
  return {
    type: "simple-marker",
    style: "circle",
    color: hexToArcgisColor(hex, 200),
    size: 8,
    outline: {
      color: hexToArcgisColor(DATA_VIZ.brand.greenDeep, 220),
      width: 0.8,
    },
  }
}

/** A clean single-symbol renderer in the brand green. */
export function buildSimpleRendererValue(
  kind: GeometryKind,
): Record<string, unknown> {
  return { type: "simple", symbol: buildSymbol(kind, DATA_VIZ.brand.green) }
}

/**
 * A five-class class-breaks starter over a numeric field, colored with the
 * brand green ramp. The break values are even 0-100 placeholders — the
 * operator (or the AI) tunes them to the field's real distribution; the
 * live preview shows the effect immediately.
 */
export function buildClassBreaksRendererValue(
  kind: GeometryKind,
  fieldName: string,
): Record<string, unknown> {
  const breaks = [0, 20, 40, 60, 80, 100]
  return {
    type: "class-breaks",
    field: fieldName,
    defaultSymbol: buildSymbol(kind, DATA_VIZ.semantic.neutral),
    classBreakInfos: DATA_VIZ.greenRamp.map((hex, index) => ({
      minValue: breaks[index],
      maxValue: breaks[index + 1],
      label: `${breaks[index]} – ${breaks[index + 1]}`,
      symbol: buildSymbol(kind, hex),
    })),
  }
}

/** Numeric service fields — class-breaks / number-range candidates. */
export function isNumericFieldType(type: string | undefined) {
  return /esriFieldType(Integer|SmallInteger|Double|Single|BigInteger)/i.test(
    type ?? "",
  )
}

// --- Filter builder ---

type FilterControlType = "select" | "number-range" | "date-range"

/** The console filter control type a service field maps to; null = not filterable. */
export function getFilterControlType(
  field: LayerServiceField,
): FilterControlType | null {
  if (isNumericFieldType(field.type)) return "number-range"
  if (/esriFieldTypeString/i.test(field.type ?? "")) return "select"
  if (/esriFieldTypeDate/i.test(field.type ?? "")) return "date-range"
  return null
}

/** Field names already present in the Filters draft (root array). */
export function getFilterFieldNames(filters: unknown[]): string[] {
  return filters
    .map((entry) =>
      entry && typeof entry === "object"
        ? (entry as Record<string, unknown>).field
        : undefined,
    )
    .filter((field): field is string => typeof field === "string")
}

/**
 * A starter filter control for one service field, with the console's
 * infer-from-data options on so it works without hand-tuned config.
 */
export function buildFilterControlValue(
  field: LayerServiceField,
  type: FilterControlType,
): Record<string, unknown> {
  const config =
    type === "select"
      ? { inferOptions: true }
      : type === "number-range"
        ? { inferMinMax: true }
        : {}
  return {
    field: field.name,
    type,
    label: field.alias || field.name,
    config,
  }
}
